import { useState } from "react";

import styles from "./Settings.module.css";
import { useReturnNotice } from "./useReturnNotice";
import { Button } from "../../components/Button";
import { Pill } from "../../components/Pill";
import { ApiError } from "../../data/api/client";
import {
  type ApiAvailableRepo,
  type ApiConnection,
  disconnectConnection,
  getLinkIntent,
  linkRepository,
  startConnection,
  useAvailableRepositories,
  useConnections,
} from "../../data/api/connections";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { relTime } from "../../lib/time";
import { useNav } from "../../routing/useNav";

const providerLabel = (provider: string): string => {
  if (provider === "github") {
    return "GitHub";
  }
  return provider;
};

const providerIcon = (provider: string) =>
  provider === "github" ? (
    <Icon.Github size={20} style={{ color: "var(--ink-2)" }} />
  ) : (
    <Icon.Server size={20} style={{ color: "var(--ink-2)" }} />
  );

const statusPill = (c: ApiConnection) => {
  if (c.status === "active") {
    return <Pill status="done">Connected</Pill>;
  }
  if (c.status === "degraded") {
    return <Pill status="needs-input">Degraded</Pill>;
  }
  return <Pill dotColor="var(--ink-4)">Disabled</Pill>;
};

// What the GitHub App install callback bounces back to Settings with when there
// is nothing new in the connections list to speak for itself
// (routes/oauth.ts).
const RETURN_NOTICES: Readonly<Record<string, string>> = {
  install_requested:
    "Installation requested. An owner of that organisation has to approve it before Tinsmith can see its repositories — connect again once they have.",
  installed: "The GitHub App was installed.",
};

export const Connections = () => {
  const { connections, loading, refetch } = useConnections();
  const { workspaceId } = useCurrentWorkspace();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const returned = useReturnNotice("github", RETURN_NOTICES);

  const disconnect = (id: string): void => {
    setBusy(id);
    void disconnectConnection(id)
      .then(refetch)
      .catch(() => undefined)
      .finally(() => setBusy(null));
  };

  // A connection changing state (install popup completing, a disconnect from
  // another tab) fans out `connection.updated` on the workspace scope; pull the
  // fresh list rather than showing stale status.
  useRealtimeScope(workspaceId ? `workspace:${workspaceId}` : null, (event) => {
    if (event.kind === "connection.updated" || event.kind === "resync") {
      refetch();
    }
  });

  // Connecting is not a once-only act: each GitHub account or organisation is
  // its own installation and its own connection row, so a workspace can hold
  // several. Say so once there's already one connected.
  const hasGitHub = connections.some((c) => c.provider === "github");

  // A live notice (start failed, App unconfigured) wins over the one the
  // install callback left in the URL.
  const banner = notice ?? returned;

  // Begin the GitHub connection: the server hands back the App-install URL to
  // send the browser to. When it's null the App isn't configured on this
  // server, so we surface that rather than navigating.
  //
  // Which account to install on is github.com's question to ask, not ours: its
  // picker lists exactly the accounts the viewer may install on, and the
  // callback records whichever one comes back. Adding an org is the same button
  // as the first connect.
  const connect = (): void => {
    setBusy("start:github");
    setNotice(null);
    void startConnection("github", workspaceId ?? undefined)
      .then((res) => {
        if (res.redirect_to) {
          window.location.href = res.redirect_to;
        } else {
          setNotice(
            "The Tinsmith GitHub App isn't configured on this server, so the connection can't be completed here.",
          );
        }
      })
      .catch((e: unknown) =>
        // The server's message names the login it couldn't find, or the limit
        // it ran into; either beats "try again".
        setNotice(
          e instanceof ApiError
            ? e.message
            : "Couldn't start the connection. Try again.",
        ),
      )
      .finally(() => setBusy(null));
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Connections</h3>
        <span className={styles.grow} />
        <Button
          disabled={busy === "start:github"}
          onClick={connect}
          size="sm"
          variant="accent"
        >
          <Icon.Github size={13} />{" "}
          {hasGitHub ? "Add GitHub org" : "Connect GitHub"}
        </Button>
      </div>
      {banner ? <div className={styles.returnNotice}>{banner}</div> : null}
      <div className={styles.card}>
        {loading && connections.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>Loading connections…</span>
          </div>
        ) : null}
        {!loading && connections.length === 0 ? (
          <div className={styles.row}>
            <span className={styles.help}>
              No providers connected yet. Connect one to link repositories and
              start agents.
            </span>
          </div>
        ) : null}
        {connections.map((c) => (
          <ConnectionRow
            busy={busy === c.public_id}
            connection={c}
            key={c.public_id}
            onDisconnect={() => disconnect(c.public_id)}
            onLinked={refetch}
            workspaceId={workspaceId}
          />
        ))}
      </div>
    </section>
  );
};

// One connected provider: its state, its repository list, and the actions over
// both. The list lives here rather than in the picker below because its
// refresh control belongs on the connection — the thing being refreshed — not
// inside the picker it feeds.
const ConnectionRow = ({
  busy,
  connection,
  onDisconnect,
  onLinked,
  workspaceId,
}: {
  readonly busy: boolean;
  readonly connection: ApiConnection;
  readonly onDisconnect: () => void;
  readonly onLinked: () => void;
  readonly workspaceId: string | null;
}) => {
  const linkable =
    connection.provider === "github" && connection.status === "active";
  // Fetched as soon as the connection renders: the server serves its cached
  // list (fetching from the provider itself the first time), so the list below
  // is on screen with the connection rather than a click and a wait away.
  const repos = useAvailableRepositories(
    linkable ? connection.public_id : null,
  );
  return (
    <div>
      <div className={styles.row}>
        {providerIcon(connection.provider)}
        <div className={styles.grow}>
          <div className={styles.label}>
            {providerLabel(connection.provider)} · {connection.display_name}
          </div>
          {/* A refresh that failed keeps the cached list on screen, so this is
              where it gets to say why. */}
          <div className={repos.error ? styles.emailError : styles.help}>
            {repos.error
              ? repos.error.message
              : connection.last_error
                ? connection.last_error
                : connection.last_synced_at
                  ? `last sync ${relTime(Date.parse(connection.last_synced_at))}`
                  : "not synced yet"}
          </div>
        </div>
        {statusPill(connection)}
        {/* The actions sit together in their own tight group. Left loose in
            the row they inherited its 16px gap on top of their own margin,
            which read as three unrelated controls rather than one cluster.
            No `saveGhost` on them: that class paints its content `--paper`
            for the SaveBar's near-black pill, and this card's background IS
            `--paper`. The plain ghost variant keeps `--ink` from `.btn`. */}
        <div className={styles.rowActions}>
          {linkable ? (
            <Button
              disabled={repos.refreshing}
              icon
              onClick={repos.refresh}
              size="sm"
              title="Fetch the repository list from GitHub again"
              variant="ghost"
            >
              <Icon.Refresh size={12} />
            </Button>
          ) : null}
          <Button
            disabled={busy}
            onClick={onDisconnect}
            size="sm"
            variant="ghost"
          >
            Disconnect
          </Button>
        </div>
      </div>
      {linkable ? (
        <LinkRepo
          connectionId={connection.public_id}
          loading={repos.loading}
          onLinked={onLinked}
          refetch={repos.refetch}
          repositories={repos.repositories}
          workspaceId={workspaceId}
        />
      ) : null}
    </div>
  );
};

// The link intent settles asynchronously: a worker inserts the repositories row
// and fills in `repository`. Poll the intent until that appears (or it fails) so
// the caller can navigate straight to the new repo's page.
const LINK_POLL_INTERVAL_MS = 1000;
const LINK_POLL_MAX_ATTEMPTS = 30;

const pollLinkedRepoId = async (
  connectionId: string,
  linkId: string,
): Promise<string | null> => {
  for (let attempt = 0; attempt < LINK_POLL_MAX_ATTEMPTS; attempt += 1) {
    const intent = await getLinkIntent(connectionId, linkId).catch(() => null);
    if (!intent) {
      return null;
    }
    if (intent.repository) {
      return intent.repository.public_id;
    }
    if (intent.status === "failed") {
      return null;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, LINK_POLL_INTERVAL_MS);
    });
  }
  return null;
};

// Minimal repo-linking (the wizard was removed): list the connection's
// repositories and link one with a click. Linked ones stay listed, marked, so
// the list reads as the whole installation. No scan — the link intent's first
// sync handles the rest.
//
// Always rendered, never behind a disclosure: the list is the server's cached
// view of the connection, fetched by the row above, so there is nothing to
// wait for and nothing a click could usefully defer — linking is one click
// from opening Settings. Picking up a repository added on GitHub since then
// is the row's refresh button.
const LinkRepo = ({
  connectionId,
  loading,
  onLinked,
  refetch,
  repositories,
  workspaceId,
}: {
  readonly connectionId: string;
  readonly loading: boolean;
  readonly onLinked: () => void;
  readonly refetch: () => void;
  readonly repositories: ReadonlyArray<ApiAvailableRepo>;
  readonly workspaceId: string | null;
}) => {
  const nav = useNav();
  const [linking, setLinking] = useState<string | null>(null);

  const link = (repo: ApiAvailableRepo): void => {
    if (!workspaceId) {
      return;
    }
    setLinking(repo.provider_repo_id);
    void linkRepository(connectionId, {
      base_branch: repo.default_branch,
      provider_full_name: repo.provider_full_name,
      provider_repo_id: repo.provider_repo_id,
      workspace_id: workspaceId,
    })
      .then(async ({ link_intent }) => {
        onLinked();
        // Wait for the intent to produce a repositories row, then land on it.
        const repoId = await pollLinkedRepoId(
          connectionId,
          link_intent.public_id,
        );
        refetch();
        if (repoId) {
          nav.repo(repoId);
        }
      })
      .catch(() => undefined)
      .finally(() => setLinking(null));
  };

  // The list is only blank while the very first fetch is in flight; a refresh
  // keeps the current rows on screen and swaps them when it lands.
  const firstLoad = loading && repositories.length === 0;
  return (
    <div className={styles.repoPicker}>
      {firstLoad ? (
        <div className={styles.help}>Loading repositories…</div>
      ) : repositories.length === 0 ? (
        <div className={styles.help}>
          No repositories. Add repos to the app&apos;s install on GitHub, then
          refresh.
        </div>
      ) : (
        repositories.map((r) => (
          <div
            className={styles.row}
            key={r.provider_repo_id}
            style={{ paddingLeft: 0 }}
          >
            <span
              className={cx(styles.grow, r.already_linked && styles.repoLinked)}
            >
              {r.provider_full_name}
              {r.private ? " · private" : ""}
            </span>
            {r.already_linked ? (
              <Pill status="done">Linked</Pill>
            ) : (
              <Button
                disabled={linking === r.provider_repo_id}
                onClick={() => link(r)}
                size="sm"
              >
                {linking === r.provider_repo_id ? "Linking…" : "Link"}
              </Button>
            )}
          </div>
        ))
      )}
    </div>
  );
};
